import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatMXN } from "../lib/format";
import { CATEGORY_LABEL } from "../lib/brand";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const emptyProduct = {
  title: "", category: "curso", sector: "General", ec_code: "", description: "",
  long_description: "", price: 0, old_price: "", duration: "", modality: "100% En línea",
  image: "", features: "", featured: false, active: true,
};

export default function Admin() {
  const { user, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) navigate("/login");
  }, [user, loading, isAdmin]);

  const load = () =>
    api.get("/products", { params: { include_inactive: true } }).then((res) => setProducts(res.data));

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const openCreate = () => { setEditing(null); setForm(emptyProduct); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...emptyProduct, ...p,
      old_price: p.old_price ?? "",
      features: (p.features || []).join(", "),
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        old_price: form.old_price === "" ? null : Number(form.old_price),
        features: form.features
          ? form.features.split(",").map((f) => f.trim()).filter(Boolean)
          : [],
        gallery: [],
      };
      if (editing) {
        await api.put(`/products/${editing}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/products", payload);
        toast.success("Producto creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/products/${deleteId}`);
      toast.success("Producto eliminado");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const field = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-forest";

  if (!isAdmin) return null;

  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-brand-navy">Panel de administrador</h1>
          <p className="text-sm text-slate-500">Gestiona el catálogo de certificaciones</p>
        </div>
        <button
          data-testid="admin-create-course-button"
          onClick={openCreate}
          className="flex items-center gap-2 rounded-xl brand-forest px-5 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.03]"
        >
          <Plus className="h-4 w-4" /> Nuevo producto
        </button>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-bold uppercase tracking-wide text-slate-500">
          <span>Producto</span><span className="hidden sm:block">Categoría</span><span>Precio</span><span>Acciones</span>
        </div>
        {products.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-16 text-slate-400">
            <Package className="h-10 w-10" /> Sin productos aún
          </div>
        )}
        {products.map((p) => (
          <div key={p.id} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 border-b border-slate-50 px-5 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <img src={p.image} alt="" className="h-11 w-11 flex-none rounded-lg object-cover" />
              <div className="min-w-0">
                <p className="line-clamp-1 text-sm font-semibold text-brand-navy">{p.title}</p>
                {!p.active && <span className="text-[11px] font-bold text-rose-500">Inactivo</span>}
              </div>
            </div>
            <span className="hidden text-xs font-semibold text-slate-500 sm:block">{CATEGORY_LABEL[p.category] || p.category}</span>
            <span className="text-sm font-bold text-brand-navy">{formatMXN(p.price)}</span>
            <div className="flex gap-1">
              <button data-testid={`admin-edit-course-${p.id}`} onClick={() => openEdit(p)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
                <Pencil className="h-4 w-4" />
              </button>
              <button data-testid={`admin-delete-course-${p.id}`} onClick={() => setDeleteId(p.id)} className="rounded-lg p-2 text-rose-500 hover:bg-rose-50">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-brand-navy">
              {editing ? "Editar producto" : "Nuevo producto"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-semibold">Título *</label>
              <input data-testid="admin-form-title" value={form.title} onChange={set("title")} className={field} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold">Categoría *</label>
              <select data-testid="admin-form-category" value={form.category} onChange={set("category")} className={field}>
                <option value="taller">Taller</option>
                <option value="curso">Curso</option>
                <option value="diplomado">Diplomado</option>
                <option value="master">Master</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold">Sector</label>
              <input value={form.sector} onChange={set("sector")} className={field} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold">Estándar / Código EC</label>
              <input value={form.ec_code} onChange={set("ec_code")} className={field} placeholder="EC0217.01" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold">Duración</label>
              <input value={form.duration} onChange={set("duration")} className={field} placeholder="40 horas" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold">Precio (MXN) *</label>
              <input data-testid="admin-form-price" type="number" value={form.price} onChange={set("price")} className={field} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold">Precio anterior (opcional)</label>
              <input type="number" value={form.old_price} onChange={set("old_price")} className={field} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold">Modalidad</label>
              <input value={form.modality} onChange={set("modality")} className={field} />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-semibold">URL de imagen *</label>
              <input data-testid="admin-form-image" value={form.image} onChange={set("image")} className={field} placeholder="https://…" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-semibold">Descripción corta</label>
              <textarea value={form.description} onChange={set("description")} rows={2} className={field} />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-semibold">Descripción larga</label>
              <textarea value={form.long_description} onChange={set("long_description")} rows={3} className={field} />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-semibold">Incluye (separado por comas)</label>
              <input value={form.features} onChange={set("features")} className={field} placeholder="Constancia oficial, Asesor, Material" />
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={form.featured} onChange={set("featured")} /> Destacado
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={form.active} onChange={set("active")} /> Activo (visible)
            </label>
          </div>
          <button
            data-testid="admin-save-course-button"
            disabled={saving}
            onClick={save}
            className="mt-2 w-full rounded-xl brand-navy py-3 text-sm font-bold text-white disabled:opacity-60"
          >
            {saving ? "Guardando…" : "Guardar producto"}
          </button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este producto?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-confirm-delete" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
